import React from 'react';
import { Typography, Space, Button, Tooltip, Badge } from 'antd';
import { 
  CopyOutlined, 
  DownloadOutlined, 
  ClockCircleOutlined,
  DollarOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { getStatusInfo, copyToClipboard } from '../utils';
import './JobHeader.css';

const JobHeader = ({ data, onDownloadAll, onCopyUrls }) => {
  const statusInfo = getStatusInfo(data.status);
  const isImageJob = !!data.image_job_id;
  const jobId = isImageJob ? data.image_job_id : data.workflow_job_id;
  const jobType = isImageJob ? 'Image Job' : 'Workflow Job';
  
  const handleCopyId = () => {
    copyToClipboard(jobId);
  };
  
  const formatDuration = (duration) => {
    const seconds = parseFloat(duration);
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };
  
  return (
    <div className="job-header">
      <div className="job-header-top">
        <div className="job-header-left">
          <Badge 
            status={data.status === 'processing' ? 'processing' : 'default'} 
            color={statusInfo.color}
            text={
              <Space size={8}>
                <Typography.Text className="job-type-badge">
                  {jobType}
                </Typography.Text>
                <Typography.Text className="job-id" ellipsis>
                  <Tooltip title={jobId}>
                    {jobId.substring(0, 8)}...
                  </Tooltip>
                </Typography.Text>
              </Space>
            }
          />
        </div>
        
        <div className="job-header-right">
          <Space size={8}>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={handleCopyId}
              className="action-btn"
            >
              Copy ID
            </Button>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={onCopyUrls}
              className="action-btn"
            >
              Copy URLs
            </Button>
            <Button
              size="small"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={onDownloadAll}
              className="action-btn primary"
            >
              Download All
            </Button>
          </Space>
        </div>
      </div>
      
      <div className="job-header-meta">
        <Space size={16} wrap>
          <div className="meta-item">
            <ClockCircleOutlined className="meta-icon" />
            <span className="meta-label">Created</span>
            <span className="meta-value">{formatDate(data.created_at)}</span>
          </div>
          
          {data.duration && (
            <div className="meta-item">
              <ThunderboltOutlined className="meta-icon" />
              <span className="meta-label">Duration</span>
              <span className="meta-value">{formatDuration(data.duration)}</span>
            </div>
          )}
          
          {data.cost_to_client > 0 && (
            <div className="meta-item">
              <DollarOutlined className="meta-icon" />
              <span className="meta-label">Cost</span>
              <span className="meta-value cost">${data.cost_to_client.toFixed(4)}</span>
            </div>
          )}
          
          {(data.model_name || data.workflow_definition_name) && (
            <div className="meta-item">
              <span className="meta-label">Model</span>
              <span className="meta-value model">
                {data.model_name || data.workflow_definition_name}
              </span>
            </div>
          )}
        </Space>
      </div>
      
      <div 
        className="job-header-accent"
        style={{ background: statusInfo.color }}
      />
    </div>
  );
};

export default JobHeader;
